// data/zones/zone3.ts — THE PALE COURT.
// DATA ONLY. Numbers, tables, ASCII maps. No behaviour lives here — the zone's
// mechanic is content/zones/zone3.ts, the fight is content/bosses/boss3.ts, and
// the systems that read this are game/*.
//
// DESIGN §5 Zone 3: "the dead don't stay dead — every kill leaves a husk that
// re-forms unless you seal it, and something is racing you to the husks."

import type {
  EnemyDef,
  GeneratorDef,
  ProjectileDef,
  RoomTemplate,
  ZoneConfig,
  ZonePalette,
} from '../../types'
import { TUNING } from '../tuning'
import { ZONE1_ENEMIES } from './zone1'

// ---------------------------------------------------------------- palette
// A necropolis court: white marble and bone walls over cold pale flagstone, lit
// by its own silver glow rather than by fire. `torches: 0` is deliberate — the
// Pale Court has no warm light anywhere in it, which is the first thing that
// tells a player at a glance that they are not in the Broken Gate any more.

export const ZONE3_PALETTE: ZonePalette = {
  floor: '#8d93a3',
  floorAlt: '#9aa1b1',
  wall: '#d9d7c9',
  wallEdge: '#f5f3e7',
  accent: '#b9a6dd',
  glow: '#dce7ff',
  bg: '#111420',
  torches: 0,
}

// ---------------------------------------------------------------- constants
// The zone's own rule, in one block. Every number the husk system reads.

export const ZONE3_RULES = {
  /** Seconds a husk lies there before it re-forms the body (DESIGN §5). */
  huskSeconds: 4,
  /** Seconds left once a Gravetender finishes tending it. */
  tendedSeconds: 1.5,
  /** Seconds a Gravetender must stand over a husk to tend it. */
  tendChannel: 1.5,
  /** Gloom added by every resurrection — sloppiness darkens the zone. */
  reformGloom: 0.03,
  /** Embers paid for a seal (balance knob lives in tuning.economy). */
  sealEmbers: TUNING.economy.huskSealEmber,
  /** Extra px of forgiveness on the Sexton's Spade walk-over seal. */
  walkSealSlack: 6,
  /** Defs that leave a husk when they die. Gravetenders are already empty. */
  huskSources: ['courtier_blade', 'wailer', 'palerat'] as string[],
  /** The husk body's def id. */
  huskId: 'pale_husk',
  /**
   * Suffix of the re-formed twin of each body (see ZONE3_REFORMED).
   *
   * FLAGGED INTERPRETATION. A re-form is the same enemy standing back up, so it
   * must not be a second payday: without this, letting a Courtier re-form and
   * killing it again pays 3 + 3 embers while sealing it pays 3 + 1, and the
   * zone quietly rewards exactly the sloppiness it is built to punish (a 2x
   * ember faucet against zones 1-2 — the failure mode of D-012 and D-015).
   * DESIGN §5 prices sloppiness in Gloom; it does not price it in embers.
   * So a re-formed body is spawned as an identical twin def that pays nothing.
   * To undo: delete ZONE3_REFORMED and the suffix lookup in content/zones/zone3.
   */
  reformedSuffix: '_r',
} as const

// ---------------------------------------------------------------- projectiles

export const ZONE3_PROJECTILES: ProjectileDef[] = [
  // Readability law: area denial reads green. A Wailer's grief does not chase
  // and does not aim — it expands, slowly, and the gaps between shards widen
  // with the radius, so the counterplay is legible from the first ring.
  {
    id: 'griefshard',
    kind: 'straight',
    speed: 74,
    damage: 7,
    radius: 7,
    color: '#a8e878',
    lifetime: 4.2,
  },
]

// ---------------------------------------------------------------- enemies

/**
 * DESIGN §5 Zone 3 names the Palerat as the Gnawling "re-dressed … same body,
 * court livery — deliberate roster reuse, composition is the point". So it is
 * literally the zone 1 body: the same def spread into a new id, the same
 * painter, court colours, and a small tuning nudge. Reuse is the statement.
 */
const GNAWLING: EnemyDef = ZONE1_ENEMIES.find((e) => e.id === 'gnawling') as EnemyDef

export const ZONE3_ENEMIES: EnemyDef[] = [
  {
    // Grunt+: dueling gentry. Two-step lunge behind a red glint — the tell the
    // boss will use against you an hour later, taught here by a body you can
    // afford to misread.
    id: 'courtier_blade',
    name: 'Courtier Blade',
    role: 'grunt',
    hp: 30,
    speed: 76,
    radius: 11,
    touchDamage: 7,
    ai: {
      custom: 'z3-courtier',
      lungeRange: 156,
      lungeCooldown: 2.5,
      windup: 0.46,
      /** the "2-step" of DESIGN's two-step lunge */
      steps: 2,
      stepSpeed: 430,
      stepTime: 0.18,
      stepGap: 0.15,
      strikeArc: 1.3,
      strikeRange: 52,
      strikeDamage: 1,
      recover: 0.45,
    },
    sprite: { key: 'z3_courtier', size: 32, params: { cloth: '#3b3550', bone: '#e8e4d4', glint: '#ff4a4a' } },
    emberValue: 3,
    goldChance: 0.26,
  },
  {
    // Ranged: hovering mourners. They never aim at you; they fill the floor.
    // Low HP is their stagger — DESIGN calls them "staggered by hits", and the
    // honest reading of that in a game where only the Maul staggers is that
    // anything that lands interrupts them.
    id: 'wailer',
    name: 'Wailer',
    role: 'ranged',
    hp: 20,
    speed: 58,
    radius: 11,
    touchDamage: 5,
    ai: {
      custom: 'z3-wailer',
      keepRange: 250,
      minRange: 150,
      fireRange: 330,
      fireCooldown: 4.2,
      windup: 0.58,
      rings: 2,
      ringGap: 0.5,
      shards: 9,
      projectile: 'griefshard',
    },
    sprite: { key: 'z3_wailer', size: 32, params: { shroud: '#4a4560', veil: '#ded8c6', grief: '#a8e878' } },
    emberValue: 4,
    goldChance: 0.3,
  },
  {
    // Support, and the reason every kill is now a job: it does not want you, it
    // wants the corpse. Priority target, and it says so by ignoring you.
    id: 'gravetender',
    name: 'Gravetender',
    role: 'support',
    hp: 28,
    speed: 92,
    radius: 11,
    touchDamage: 0,
    ai: {
      custom: 'z3-gravetender',
      sprintMul: 1.55,
      tendRange: 26,
      bandMin: 190,
      bandMax: 285,
      claimPenalty: 420,
    },
    sprite: { key: 'z3_gravetender', size: 30, params: { robe: '#4c4256', bone: '#ded8c6', lamp: '#c79bff' } },
    emberValue: 5,
    goldChance: 0.34,
  },
  {
    ...GNAWLING,
    id: 'palerat',
    name: 'Palerat',
    hp: 16,
    speed: 124,
    ai: { ...GNAWLING.ai, lungeCooldown: 1.8, lungeRange: 104 },
    sprite: { key: 'gnawling', size: 24, params: { color: '#ddd7c4', eye: '#c79bff' } },
    emberValue: 1,
    goldChance: 0.12,
  },
  {
    // THE HUSK. It rides the enemy pool because that is the only way one hit
    // from every weapon, splash, trail and potion already means "sealed" — but
    // it is tagged `gloomspawn` on spawn (content/zones/zone3.ts) so it can
    // never block a room clear and never pays a kill. emberValue MUST stay 0
    // (D-012): the seal pays through the mechanic, once, by hand.
    id: 'pale_husk',
    name: 'Pale Husk',
    role: 'swarm',
    hp: 1,
    speed: 0,
    radius: 11,
    touchDamage: 0,
    ai: { custom: 'z3-husk' },
    sprite: { key: 'z3_husk', size: 30, params: { glow: '#dfe9ff', core: '#6f7ba0' } },
    emberValue: 0,
    goldChance: 0,
  },
]

// ------------------------------------------------- the re-formed twins
// Trivial table builder (ARCHITECTURE §3 allows those in data): the same body,
// the same brain, the same stats, the same NAME so the run summary still groups
// them under one family — and zero payout, because standing back up is not a
// second life's worth of embers. See ZONE3_RULES.reformedSuffix for the why.

const REFORM_SOURCES = ZONE3_ENEMIES.filter((e) => ZONE3_RULES.huskSources.indexOf(e.id) >= 0)

export const ZONE3_REFORMED: EnemyDef[] = REFORM_SOURCES.map((def) => ({
  ...def,
  id: def.id + ZONE3_RULES.reformedSuffix,
  emberValue: 0,
  goldChance: 0,
}))

// ---------------------------------------------------------------- generators

export const ZONE3_GENERATORS: GeneratorDef[] = [
  {
    id: 'gen_reliquary',
    name: 'Reliquary Shrine',
    hp: 100,
    radius: 16,
    spawns: 'courtier_blade',
    interval: 5.6,
    burst: 1,
    cap: 3,
    sprite: { key: 'z3_gen_reliquary', size: 42, params: { bone: '#ece7d6', cloth: '#5a3f6e' } },
    emberValue: 5,
  },
  {
    id: 'gen_boneorgan',
    name: 'Bone-Organ',
    hp: 82,
    radius: 16,
    spawns: 'wailer',
    interval: 7.2,
    burst: 1,
    cap: 2,
    sprite: { key: 'z3_gen_boneorgan', size: 42, params: { bone: '#e4dfcc', pipe: '#b9b2a0' } },
    emberValue: 5,
  },
  {
    id: 'gen_crypt',
    name: 'Cracked Crypt',
    hp: 64,
    radius: 15,
    spawns: 'palerat',
    interval: 3.6,
    burst: 2,
    cap: 6,
    sprite: { key: 'z3_gen_crypt', size: 40, params: { stone: '#cfcbba' } },
    emberValue: 4,
  },
]

// ---------------------------------------------------------------- rooms
// 44x26 tiles at 32px, the same frame as zone 1 so the camera reads identically
// and only the rules differ. Eight combat templates in three ordered groups —
// the runner (game/zones.ts) cuts the library into roomCount contiguous groups
// and draws one from each, so a run through the Pale Court always reads:
//
//   group 1 (1 generator):  z3-antechamber, z3-lyingroom
//     — one source, few bodies, and the first husk you ever see. Learn the job.
//   group 2 (2 generators): z3-processional, z3-mourners*, z3-topiary
//     — Gravetenders arrive. Now the job has a competitor.
//   group 3 (3 generators): z3-cryptgate, z3-organloft*, z3-highcourt
//     — three sources, a gated one behind a key-carrying elite, tenders in pairs.
//   (* carries the timed vault — 'V' door into a 'v' alcove)
//
// The respite (z3-chapel) sits in the same array and is never a combat pick:
// the runner recognises it by its altar glyph.

export const ZONE3_ROOMS: RoomTemplate[] = [
  // The Antechamber — one reliquary in the open. The first Courtier you cut
  // leaves something behind, and the room is quiet enough to notice.
  {
    id: 'z3-antechamber',
    generators: ['gen_reliquary'],
    patrols: ['courtier_blade', 'palerat'],
    map: [
      '############################################',
      '#..........................................#',
      '#....@.....................................#',
      '#..........................................#',
      '#.........##....................##.........#',
      '#.........##.........G..........##.........#',
      '#.........##....................##.........#',
      '#..........................................#',
      '#...............E..........................#',
      '#..........................................#',
      '#.....########..............########.......#',
      '#..........................................#',
      '#..........................................#',
      '#......................F...................#',
      '#..........................................#',
      '#.....########..............########.......#',
      '#..........................................#',
      '#..............E.................E.........#',
      '#..........................................#',
      '#.........##....................##.........#',
      '#.........##.....$..............##.........#',
      '#.........##....................##.........#',
      '#..........................................#',
      '#.............F............................#',
      '#..........................................#',
      '#####################D######################',
    ],
  },
  // The Lying-Room — rows of biers, one cracked crypt at the far end. Palerats
  // pour in numbers, so the husks pile up fast: this is where a player learns
  // that ignoring them costs more than sealing them.
  {
    id: 'z3-lyingroom',
    generators: ['gen_crypt'],
    patrols: ['palerat', 'courtier_blade'],
    map: [
      '############################################',
      '#..........................................#',
      '#..@.......................................#',
      '#..........................................#',
      '#....##....##....##....##....##....##......#',
      '#....##....##....##....##....##....##......#',
      '#..........................................#',
      '#..............E...........................#',
      '#..........................................#',
      '#....##....##....##....##....##....##......#',
      '#....##....##....##....##....##....##......#',
      '#.........................F................#',
      'D..........................................D',
      '#..........................................#',
      '#....##....##....##....##....##....##......#',
      '#....##....##....##....##....##....##......#',
      '#........$.................................#',
      '#..............................E...........#',
      '#..........................................#',
      '#....##....##....##....##....##....##......#',
      '#....##....##....##....##....##....##......#',
      '#..F.......................................#',
      '#.....................................G....#',
      '#..........................................#',
      '#..........................................#',
      '############################################',
    ],
  },
  // The Processional — a long aisle with a screen down the middle. Reliquary on
  // one side, bone-organ on the other, and the tenders work the aisle between
  // them: you cannot watch both halves of your own kill list.
  {
    id: 'z3-processional',
    generators: ['gen_reliquary', 'gen_boneorgan'],
    patrols: ['courtier_blade', 'wailer', 'gravetender'],
    map: [
      '#################################D##########',
      '#.....................#....................#',
      '#.....................#....................#',
      '#.....G...............#.................F..#',
      '#.....................#....................#',
      '#...###########.......#...........E........#',
      '#.....................#....................#',
      '#.....................#....................#',
      '#.......####.....F....#....................#',
      '#.......####..........#....................#',
      '#.......####...............................#',
      '#...................#####..................#',
      '#...$.........E.....##.##.....E........$...#',
      '#...................#####..................#',
      '#...................#####..................#',
      '#...............................####.......#',
      '#.....................#.........####.......#',
      '#.....................#.........####.......#',
      '#.....................#....F...............#',
      '#.....................#....................#',
      '#........E............#......###########...#',
      '#.....................#....................#',
      '#.....................#...............G....#',
      '#..@..................#....................#',
      '#.....................#....................#',
      '############D###############################',
    ],
  },
  // The Mourners' Gallery — the zone's timed vault. Two sources, a wide gallery
  // of pews, and a golden door in the north-east that only answers a Warden who
  // sealed efficiently on the way here (vaultClockSeconds is deliberately tight).
  {
    id: 'z3-mourners',
    generators: ['gen_boneorgan', 'gen_crypt'],
    patrols: ['wailer', 'gravetender', 'palerat'],
    map: [
      '############################################',
      '#...........................#vvvvvvvv#.....#',
      '#...........................Vvvvvvvvv#.....#',
      '#...........................Vvvvvvvvv#.....#',
      '#....@......................#vvvvvvvv#.....#',
      '#...........................##########.....#',
      '#.....####.........................E.......#',
      '#.....####........F........................#',
      '#.....####.................................#',
      '#..........................................#',
      '#...........G..............................#',
      '#..........................................#',
      '#.......########..............########.....#',
      'D..........................................#',
      '#..........................................#',
      '#..............E...........................#',
      '#..........................$..............F#',
      '#..........................................#',
      '#....####..................................#',
      '#....####...........E......................#',
      '#....####..................................#',
      '#..........................................#',
      '#..................................G.......#',
      '#.......F..................................#',
      '#..........................................#',
      '############################################',
    ],
  },
  // The Dead Topiary — hedges of bone-white deadwood in a grid. Sightlines are
  // short, so a husk you left behind comes back out of a corner you already
  // cleared. Crypt and reliquary at opposite ends of the maze.
  {
    id: 'z3-topiary',
    generators: ['gen_crypt', 'gen_reliquary'],
    patrols: ['courtier_blade', 'gravetender', 'palerat'],
    map: [
      '############################################',
      '#..........................................#',
      '#..@..........F............................#',
      '#..........................................#',
      '#....###...###...###...###...###...###.....#',
      '#....###...###...###...###...###...###.....#',
      '#..........................................#',
      '#............E.......................G.....#',
      '#..........................................#',
      '#....###...###...###...###...###...###.....#',
      '#....###...###...###...###...###...###.....#',
      '#..........................................#',
      'D...............$..........................D',
      '#..........................................#',
      '#....###...###...###...###...###...###.....#',
      '#....###...###...###...###...###...###.....#',
      '#..........................................#',
      '#.......E....................E.............#',
      '#..........................................#',
      '#....###...###...###...###...###...###.....#',
      '#....###...###...###...###...###...###.....#',
      '#..........................................#',
      '#....G..........................F..........#',
      '#..........................................#',
      '#..........................................#',
      '############################################',
    ],
  },
  // The Crypt Gate — three sources, the last one behind iron. The key walks the
  // room on a marked elite, and every body you cut to reach it leaves work.
  {
    id: 'z3-cryptgate',
    generators: ['gen_crypt', 'gen_reliquary', 'gen_boneorgan'],
    patrols: ['courtier_blade', 'gravetender', 'wailer'],
    map: [
      '############################################',
      '#..........................................#',
      '#..........................................#',
      '#..@..........F.........E..................#',
      '#...........................################',
      '#...........................#..............#',
      '#.......###.................#..............#',
      '#.......###.................#.......G......#',
      '#.......###.........K.......LLL............#',
      '#...........................#..............#',
      '#................####.......#....$.....C...#',
      '#................####.......#..............#',
      '#.....E..........####.......################',
      '#................####......................#',
      '#...........G..............................#',
      '#........................E.................#',
      '#...F...........#..........................#',
      '#.......###.....#..........................#',
      '#.......###.....#..........................#',
      '#.......###.....#..........................#',
      '#..##############.............F............#',
      '#.....................G....................#',
      '#.........................F................#',
      '#.......E..................................#',
      '#..........................................#',
      '#####################D######################',
    ],
  },
  // The Organ Loft — the zone's other timed vault, tucked in the south-west so
  // the fast route and the safe route disagree. Two organs and a crypt, wailers
  // filling the middle with rings while tenders work the edges.
  {
    id: 'z3-organloft',
    generators: ['gen_boneorgan', 'gen_reliquary', 'gen_crypt'],
    patrols: ['wailer', 'courtier_blade', 'gravetender'],
    map: [
      '############################################',
      '#..........................................#',
      '#..@...........E...........................#',
      '#..........................................#',
      '#........####..................####........#',
      '#........####......G...........####........#',
      '#........####..................####........#',
      '#..........................................#',
      '#.................F........................#',
      '#..........................................#',
      'D.........................E................#',
      '#..........................................#',
      '#.....############......############.......#',
      '#..........................................#',
      '#...........G.....................$........#',
      '#..........................................#',
      '#..........................E...............#',
      '#..........................................#',
      '#....########..............................#',
      '#....#vvvvvv#..........F...................#',
      '#....#vvvvvvV..............................#',
      '#....#vvvvvvV.................G............#',
      '#....########..............................#',
      '#..........................................#',
      '#.......F..................................#',
      '############################################',
    ],
  },
  // The High Court — the widest room in the zone and the last thing before the
  // chapel. Three sources at the three points of the hall, tenders in pairs, and
  // nowhere to hide from your own leftovers.
  {
    id: 'z3-highcourt',
    generators: ['gen_reliquary', 'gen_boneorgan', 'gen_crypt'],
    patrols: ['courtier_blade', 'wailer', 'gravetender'],
    map: [
      '############################################',
      '#..........................................#',
      '#.....G.............E................G.....#',
      '#..........................................#',
      '#....##.....................##.....##......#',
      '#....##.....................##.....##......#',
      '#..........................................#',
      '#........F.........................F.......#',
      '#..........................................#',
      '#........########........########..........#',
      '#..........................................#',
      'D.............E...................E........D',
      '#..........................................#',
      '#............####......####................#',
      '#............####......####................#',
      '#..........................................#',
      '#....$...............E...............$.....#',
      '#..........................................#',
      '#........########........########..........#',
      '#..........................................#',
      '#....##.....................##.....##......#',
      '#....##.....................##.....##......#',
      '#..........................................#',
      '#..@.........F...........G.........F.......#',
      '#..........................................#',
      '############################################',
    ],
  },
  // ------------------------------------------------------------- the respite
  // The Pale Chapel: a cold nave with an empty bier down the middle, the Altar
  // in the west transept, the fountain in the east, and the throne-hall door
  // sealed in the north wall. Nothing spawns; the Gloom stops filling.
  {
    id: 'z3-chapel',
    generators: [],
    patrols: [],
    // The nave runs straight from the Warden's step to the stair: a respite is
    // the one room that must never make you fight the geometry.
    map: [
      '#################S################',
      '#................................#',
      '#................................#',
      '#....##....................##....#',
      '#....##....................##....#',
      '#................................#',
      '#.......A.................N......#',
      '#................................#',
      '#....####..............####......#',
      '#....####..............####......#',
      '#................................#',
      '#................................#',
      '#..............@.................#',
      '#................................#',
      '#................................#',
      '#................................#',
      '#................................#',
      '##################################',
    ],
  },
]

// ============================================================================
// BOSS 3 — THE TWICE-CROWNED (DESIGN §5 Zone 3)
// "Verb: seal the kill — the fight's own death must be denied."
// Every literal the fight reads lives here; content/bosses/boss3.ts is behaviour.
// ============================================================================

// ---------------------------------------------------------------- boss arena
// The throne hall. 44x21 to match the framing pass (D-016) so the whole duel
// fits on screen. The THRONE stands upstage centre-north as solid furniture —
// it is drawn, it is looming, and at the hinge it is the thing the Crown is
// running for. Two low screens flank the floor so the cape-blink has geometry
// to disappear behind; nothing else obstructs a duel.
export const ZONE3_BOSS_ARENA: RoomTemplate = {
  id: 'z3-thronehall',
  generators: [],
  patrols: [],
  map: [
    '############################################',
    '#..........................................#',
    '#..........................................#',
    '#..........................................#',
    '#..........................................#',
    '#.....####........................####.....#',
    '#..........................................#',
    '#..........................................#',
    '#..........................................#',
    '#..........................................#',
    '#..........................................#',
    '#..........................................#',
    '#..........................................#',
    '#.....####........................####.....#',
    '#..........................................#',
    '#.....F..............................F.....#',
    '#..........................................#',
    '#.....................@....................#',
    '#..........................................#',
    '#..........................................#',
    '######################D#####################',
  ],
}

// ------------------------------------------------------ the Crown, as a body
// The Crown is an ordinary enemy so that every weapon, every splash, the burn
// trail, the potion and the Maul's stagger all already know what to do with it
// — "staggerable" (DESIGN) costs nothing to honour that way. Its HP is written
// by the boss at spawn (ZONE3_BOSS.crown.hp) so the zone stat scale cannot
// drift the hinge. emberValue MUST stay 0: it is a phase of a boss, not loot.
export const ZONE3_BOSS_ENEMIES: EnemyDef[] = [
  {
    id: 'z3_crown',
    name: 'The Crown',
    role: 'stalker',
    hp: 60,
    speed: 60,
    radius: 13,
    touchDamage: 0,
    ai: { custom: 'z3-crown' },
    sprite: { key: 'z3_crown', size: 30, params: { gold: '#e8dcae', gem: '#c79bff' } },
    emberValue: 0,
    goldChance: 0,
  },
]

/**
 * Boss tuning block. Arrays indexed by phase-1 (P1, P2).
 *
 * FLAGGED INTERPRETATION (`crawlSeconds`): DESIGN §5 says "6s crawl, 60 HP".
 * The distance from where the king falls to the throne is not fixed, so a fixed
 * px/s would make the window a function of where he happened to die. The Crown
 * therefore crawls at `distance / crawlSeconds`: the window is always exactly
 * six seconds of 60 HP, wherever the duel ended. `maxCrawlSeconds` is a pure
 * softlock backstop and should never be reached.
 */
export const ZONE3_BOSS = {
  id: 'twicecrowned',
  name: 'THE TWICE-CROWNED',

  hp: 780,
  radius: 22,
  /** Tile coordinates inside ZONE3_BOSS_ARENA. */
  startTile: { tx: 22, ty: 9 },
  throneTile: { tx: 22, ty: 4 },
  /** How close the Crown must get to the throne to win the hinge. */
  throneRadius: 40,

  /** The duel band. He closes with poise and refuses to be a wall of meat. */
  walk: { speed: 104, bandMin: 92, bandMax: 176, strafe: 0.5 },

  /** The rapier combo. 3 thrusts in P1, 4 in P2 (DESIGN §5). */
  combo: {
    interval: [3.3, 2.7],
    /** the red-glint tell; >= 0.4s is the readability law's floor */
    windup: [0.52, 0.46],
    thrusts: [3, 4],
    stepTime: 0.18,
    stepGap: [0.17, 0.14],
    recover: 0.55,
    dashSpeed: 460,
    range: 60,
    arc: 1.25,
    damage: 13,
  },

  /** The cape blink: brief invulnerable dash on a learnable cadence. */
  blink: {
    interval: [6.0, 5.2],
    /** cape flourish telegraph */
    flourish: [0.44, 0.38],
    seconds: 0.26,
    distance: 215,
    /** P2 only: the vacated ground burns. */
    afterimage: { seconds: 1.4, radius: 46, dps: 26 },
  },

  /** Courtier pairs. P2's arrive Frenzied (DESIGN §5). */
  summon: {
    interval: [9.5, 8.0],
    count: 2,
    liveCap: 8,
    defId: 'courtier_blade',
    spread: 130,
  },

  /** DESIGN §5: untended husks in this room re-form in 3s, not 4. */
  huskSeconds: 3,

  /** THE HINGE. */
  crown: {
    defId: 'z3_crown',
    hp: 60,
    crawlSeconds: 6,
    maxCrawlSeconds: 18,
    /** Crown destroyed: the Gloom still drags him up, at this fraction. */
    riseFraction: 0.55,
  },

  /** Crown reached the throne: full HP and a tighter cadence. */
  enrage: { telegraphCut: 0.1, intervalMul: 0.85 },

  hinge: { shake: 11, shakeSeconds: 0.9, hitstop: 0.22 },
  death: { shake: 13, shakeSeconds: 1.1, hitstop: 0.3 },
} as const

// ---------------------------------------------------------------- zone

export const ZONE3: ZoneConfig = {
  id: 3,
  name: 'The Pale Court',
  tagline: 'Kill it twice, or seal it once.',
  palette: ZONE3_PALETTE,
  rooms: ZONE3_ROOMS,
  roomCount: 3,
  gloomFillSeconds: 90,
  // Tight on purpose (DESIGN §5: "its threshold is tight — sealing efficiently
  // is the speed skill here"). Zone 1 pays 170s for the same three rooms.
  vaultClockSeconds: 145,
  eliteRate: 0.1,
  eliteRiders: ['frenzied', 'shielded', 'gloomtouched'],
  statScale: { hp: 1.25, damage: 1.21 },
  foodPerRoom: [1, 2],
  music: {
    root: 47,
    mode: 'harmonicMinor',
    tempo: 84,
    padLevel: 0.95,
    bassLevel: 0.6,
    percLevel: 0.42,
    character: 'court',
  },
  bossId: 'twicecrowned',
}
